import {
  AdminReviewStatus,
  BusinessDocument,
  BusinessDocumentType,
  DocumentStatus,
  SupportedVerificationType,
} from "@/types/business";

export const DOCUMENT_TYPE_OPTIONS = Object.keys(SupportedVerificationType);

export type KycStatus = "verified" | "rejected" | "pending" | "unverified";

export const normalizeKycStatus = (status?: string | null): KycStatus => {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "verified" || s === "approved") return "verified";
  if (s.includes("reject")) return "rejected";
  if (s && s !== "unverified" && s !== "none") return "pending";
  return "unverified";
};

export interface StagedDocument {
  type: BusinessDocumentType;
  number: string;
  upload_url: string;
}

export interface DocumentNumberRule {
  error: string;
  hint: string;
  maxLength: number;
  pattern: RegExp;
}

export const DOCUMENT_NUMBER_RULES: Record<string, DocumentNumberRule> = {
  nin: {
    error: "NIN must be exactly 11 digits (0-9).",
    hint: "11 digits, numbers only",
    maxLength: 11,
    pattern: /^\d{11}$/,
  },
  cac: {
    error: "CAC must be 6 or 7 digits, optionally prefixed by RC or BN.",
    hint: "7-8 digits, e.g. RC-123456 or 1234567",
    maxLength: 10,
    pattern: /^(RC|BN)?\d{6,7}$/,
  },
};

export const getDocumentNumberRule = (
  type?: string | null,
): DocumentNumberRule | null => {
  const key = (type ?? "").toLowerCase();
  return DOCUMENT_NUMBER_RULES[key] ?? null;
};

export const sanitizeDocumentNumber = (
  value: string,
  type?: string | null,
): string => {
  const key = (type ?? "").toLowerCase();
  if (key === "nin") return value.replace(/\D/g, "").slice(0, 11);
  if (key === "cac")
    return value
      .replace(/[^0-9a-zA-Z]/g, "")
      .toUpperCase()
      .slice(0, 10);
  return value;
};

export const isDocumentNumberValid = (value: string, type?: string | null) => {
  const rule = getDocumentNumberRule(type);
  if (!rule) return true;
  return rule.pattern.test(value.trim());
};

export const ACCEPTED_FILE_FORMATS = [
  "image/jpeg",
  "image/png",
  "application/pdf",
];

export const MAX_FILE_SIZE_MB = 50;

export const MAX_DOCUMENTS = 3;

export interface DocumentDisplayStatus {
  label: string;
  className: string;
}

export const getDocumentDisplayStatus = (
  doc: BusinessDocument,
): DocumentDisplayStatus => {
  const isEngineApproved = doc.status === DocumentStatus.VERIFIED;
  const isEngineRejected = doc.status === DocumentStatus.REJECTED;

  const isAdminPending = doc.admin_review_status === AdminReviewStatus.PENDING;
  const isAdminApproved =
    doc.admin_review_status === AdminReviewStatus.APPROVED;
  const isAdminRejected =
    doc.admin_review_status === AdminReviewStatus.REJECTED;

  let label = "PENDING";
  let className = "bg-amber-50 text-amber-700 border-amber-200";

  if (isEngineApproved) {
    label = "APPROVED";
    className = "bg-emerald-50 text-emerald-700 border-emerald-200";
  } else if (isEngineRejected) {
    label = "REJECTED";
    className = "bg-rose-50 text-rose-700 border-rose-200";
  } else if (isAdminApproved) {
    label = "PROCESSING";
    className = "bg-blue-50 text-blue-700 border-blue-200";
  } else if (isAdminRejected) {
    label = "RE-UPLOAD REQUIRED";
    className = "bg-amber-50 text-amber-700 border-amber-200";
  } else if (isAdminPending) {
    label = "PENDING";
    className = "bg-amber-50 text-amber-700 border-amber-200";
  }

  return { label, className };
};
